import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { buildPrefillRows, checkPrefillUrl } from '../utils/profilePrefill.js';
import { requestProfilePrefill } from '../services/profilePrefillService.js';

const ACCENT = '#1d7f7b';

/**
 * "Auto-fill from your web page" (Settings > Profile and onboarding step 2).
 *
 * The therapist pastes a public page; the backend suggests values; a review
 * dialog lists only the suggestions for fields that are still EMPTY, all ticked;
 * "Fill N fields" hands the ticked rows to `onApply`, which sets them through the
 * page's own setters. Nothing is saved here: the page's existing Update/Continue
 * button does that, so its validation and save logic are unchanged.
 *
 * Props:
 * - fields: [{ key, label, section, current }] the page lets AI fill (see buildPrefillRows)
 * - optionLists: picker lists for the backend (buildPrefillOptionLists)
 * - onApply(rows), onUndo(rows): set / restore the page's fields
 * - saveLabel: the page's save button text, used in the copy ("Update", "Continue")
 * - appliedNote: optional extra sentence for the "AI filled" banner
 */
const ProfileAutofill = ({ fields, optionLists, onApply, onUndo, saveLabel = 'Update', appliedNote = '' }) => {
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null); // { severity, text }
  const [review, setReview] = useState(null); // { host, rows }
  const [checked, setChecked] = useState({});
  const [applied, setApplied] = useState([]);

  const handleFill = async () => {
    const { url: target, error } = checkPrefillUrl(url);
    if (error) {
      setUrlError(error);
      return;
    }
    setUrlError('');
    setNotice(null);
    setLoading(true);
    try {
      const data = await requestProfilePrefill(target, optionLists);
      const rows = buildPrefillRows(data, fields);
      if (rows.length === 0) {
        setNotice({
          severity: 'info',
          text: 'Nothing new to fill from that page. Fields you have already filled are never changed.',
        });
        return;
      }
      setChecked(Object.fromEntries(rows.map((row) => [row.key, true])));
      setReview({ host: data.source_host || '', rows });
    } catch (err) {
      if (err.code === 'page_unreadable') {
        setNotice({
          severity: 'error',
          title: 'We couldn’t read that page.',
          text: 'Some sites, like LinkedIn, block automatic reading. Try your clinic or professional register page, or fill the fields manually.',
        });
      } else if (err.code === 'bad_url') {
        setUrlError(err.message);
      } else {
        setNotice({ severity: 'error', text: err.message || 'Could not auto-fill right now. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedRows = review ? review.rows.filter((row) => checked[row.key]) : [];

  const handleApply = () => {
    if (!selectedRows.length) return;
    // The form rechecks current values/verification locks at apply time.
    const accepted = onApply(selectedRows);
    setApplied(Array.isArray(accepted) ? accepted : selectedRows);
    setReview(null);
  };

  const handleUndo = () => {
    onUndo(applied);
    setApplied([]);
  };

  const setAll = (value) => setChecked(Object.fromEntries((review?.rows || []).map((row) => [row.key, value])));

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
      <Box
        component="section"
        aria-labelledby="profile-autofill-title"
        sx={{ border: '1px solid #cfe3e1', borderRadius: 4, bgcolor: '#fff', p: { xs: 2, sm: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <AutoAwesomeIcon sx={{ color: ACCENT, fontSize: 20 }} aria-hidden="true" />
          <Typography id="profile-autofill-title" component="h2" sx={{ fontSize: 16, fontWeight: 700, color: '#263c40' }}>
            Auto-fill from your web page
          </Typography>
          <Chip label="New" size="small" sx={{ height: 20, fontSize: 12, fontWeight: 500, color: ACCENT, bgcolor: '#e3f2f1' }} />
        </Stack>
        <Typography variant="body2" sx={{ color: '#5f6f73', lineHeight: 1.5 }}>
          Paste a public page about you, such as your clinic bio, professional register listing or personal website.
          AI suggests details for you to review. Nothing is saved until you click {saveLabel}.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
          <TextField
            fullWidth
            size="small"
            label="Web page link"
            type="url"
            inputMode="url"
            placeholder="https://yourclinic.com/team/your-name"
            value={url}
            disabled={loading}
            onChange={(e) => { setUrl(e.target.value); setUrlError(''); if (notice) setNotice(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFill(); } }}
            error={Boolean(urlError)}
            helperText={urlError || ' '}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            onClick={handleFill}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#176864' }, textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap', minHeight: 40, px: 2.5, flexShrink: 0 }}
          >
            {loading ? 'Reading page…' : 'Auto-fill with AI'}
          </Button>
        </Stack>
        {notice && (
          <Alert severity={notice.severity} sx={{ borderRadius: 2 }}>
            {notice.title && <strong>{notice.title} </strong>}
            {notice.text}
          </Alert>
        )}
      </Box>

      {applied.length > 0 && (
        <Alert
          severity="success"
          icon={false}
          role="status"
          sx={{ mt: 2, borderRadius: 2, alignItems: 'center', '& .MuiAlert-action': { pt: 0 } }}
          action={(
            <Button variant="outlined" size="small" onClick={handleUndo} sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}>
              Undo auto-fill
            </Button>
          )}
        >
          <strong>AI filled {applied.length} {applied.length === 1 ? 'field' : 'fields'}:</strong>{' '}
          {applied.map((row) => row.label).join(', ')}. Check them, then click {saveLabel} to save.
          {appliedNote ? ` ${appliedNote}` : ''}
        </Alert>
      )}

      <Dialog open={Boolean(review)} onClose={() => setReview(null)} fullWidth maxWidth="sm" aria-labelledby="profile-autofill-review-title">
        <DialogTitle id="profile-autofill-review-title" sx={{ pb: 1 }}>Review suggested details</DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#5f6f73', mb: 1 }}>
            {review?.host ? <>Found on <strong>{review.host}</strong>. </> : null}
            Only ticked fields are filled in. Existing profile details are kept; a suggested association can replace your current selection.
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#5f6f73' }}>
              {selectedRows.length} of {review?.rows.length || 0} selected
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Button size="small" onClick={() => setAll(true)} sx={{ textTransform: 'none' }}>Select all</Button>
              <Button size="small" onClick={() => setAll(false)} sx={{ textTransform: 'none' }}>Clear</Button>
            </Stack>
          </Stack>
          {(review?.rows || []).map((row) => (
            <Stack key={row.key} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 1.25, borderTop: '1px solid #eef3f3' }}>
              <Checkbox
                checked={Boolean(checked[row.key])}
                onChange={() => setChecked((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
                inputProps={{ 'aria-labelledby': `profile-autofill-row-${row.key}` }}
                sx={{ p: 0.5, color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                  <Typography id={`profile-autofill-row-${row.key}`} variant="body2" sx={{ fontWeight: 600, color: '#263c40' }}>{row.label}</Typography>
                  {row.section && <Typography variant="caption" sx={{ color: '#5f6f73' }}>{row.section}</Typography>}
                </Stack>
                <Typography variant="body2" sx={{ color: '#263c40', mt: 0.25, overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>{row.display}</Typography>
                {row.key === 'association' && row.registrySnapshot?.association && row.registrySnapshot.association !== row.value && (
                  <Typography variant="caption" color="warning.main">Replaces {row.registrySnapshot.association}. Existing registry names and member number will be cleared to avoid mixing registrations. Review the suggested details before verifying.</Typography>
                )}
              </Box>
            </Stack>
          ))}
          <Typography variant="caption" component="p" sx={{ color: '#5f6f73', mt: 1.5 }}>
            Never filled by AI: certificates, certificate expiry, session fees and packages,
            payout details, profile picture, gender and religion.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setReview(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={selectedRows.length === 0}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#176864' }, textTransform: 'none', borderRadius: 2 }}
          >
            {selectedRows.length === 1 ? 'Fill 1 field' : `Fill ${selectedRows.length} fields`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileAutofill;
